import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import React from 'react'
import { styled } from '@mui/material/styles'
import { APPalette } from '../utils'

type tab = {
    label: React.ReactNode,
    content: React.ReactNode,
}

interface APTabsInterface {
    items: tab[]
    style?: React.CSSProperties
    onChanged?: (activeTabIndex: number, activeTabLabel: React.ReactNode) => void
    showTabs?: boolean
    activeIndex?: number
    activeVariant?: "solid" | "subtle"
}

const StyledTabs = styled(Tabs, {
    shouldForwardProp: (prop) => prop !== 'activeVariant',
})<{ activeVariant?: "solid" | "subtle" }>(({ activeVariant }) => ({
    boxShadow: activeVariant === "solid" ? '0px 4px 24px 0px rgba(0, 0, 0, 0.08)' : undefined,
    borderRadius: '12px',
    '& .MuiTabs-indicator': {
        display: 'none',
    },
    '& .MuiTabs-flexContainer': {
        border: activeVariant === "solid" ? '4px solid white' : `1px solid ${APPalette['grey-200']}`,
        borderRadius: activeVariant === "solid" ? '8px' : '12px',
        backgroundColor: APPalette["white"],
    },
    '& .MuiTab-root': {
        textTransform: 'none',
        height: "36px",
        minWidth: 0,
        padding: '12px',
        color: APPalette["grey-600"],
        backgroundColor: 'transparent',
        '&.Mui-selected': {
            backgroundColor: activeVariant === "solid" ? APPalette["brand-300"] : APPalette["brand-100"],
            color: activeVariant === "solid" ? APPalette["white"] : APPalette["brand-300"],
            borderRadius: activeVariant === "solid" ? '8px' : '12px',
        },
        flexGrow: 1,
        textAlign: 'center',
    },
}));

const StyledTabPanel = styled(TabPanel)({
    padding: '0px 0 0 0',
});

export function APTabs({ items, style, onChanged, showTabs, activeIndex, activeVariant = "subtle" }: APTabsInterface) {
    const isControlled = activeIndex !== undefined;
    const [internalValue, setInternalValue] = React.useState("0");

    const value = isControlled ? activeIndex.toString() : internalValue;
    const resolvedShowTabs = showTabs ?? items.some(item => item.label !== "");

    const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        if (!isControlled) {
            setInternalValue(newValue);
        }

        if (onChanged) {
            const tabIndex = parseInt(newValue);
            const activeTab = items[tabIndex];
            onChanged(tabIndex, activeTab?.label);
        }
    };

    return (
        <Box style={style}>
            <TabContext value={value}>
                {resolvedShowTabs && (
                    <Box>
                        <StyledTabs
                            value={value}
                            onChange={handleChange}
                            variant='fullWidth'
                            activeVariant={activeVariant}
                        >
                            {items.map((item, index) => (
                                <Tab
                                    key={index}
                                    label={item.label}
                                    value={index.toString()}
                                />
                            ))}
                        </StyledTabs>
                    </Box>
                )}
                {items.map((item, index) => (
                    <StyledTabPanel key={index} value={index.toString()}>
                        {item.content}
                    </StyledTabPanel>
                ))}
            </TabContext>
        </Box>
    );
}
